import { useEffect, useMemo, useRef, useState } from "react";
import type { CustomerItem } from "../../../../mocks/customers";
import type { PaymentRecord, OdemeTipi } from "../../../../mocks/payments";
import { useAnimals } from "../../../../hooks/useAnimals";
import { useCustomers } from "../../../../hooks/useCustomers";
import { useBankTransfers } from "../../../../hooks/useBankTransfers";
import { isBankTransferActive } from "../../../../lib/bankTransferStore";
import { matchesOptionalDateRange } from "../../../../lib/dateFilters";

interface Props {
  payment: PaymentRecord | null;
  onSave: (data: Partial<PaymentRecord>, bankTransferChanges?: { linkedIds?: string[]; unlinkedFisNos?: string[] }) => void;
  onClose: () => void;
}

interface ShareOption {
  kupeNo: string;
  hisseNo: string;
  animalId: number;
  shareId: number;
  kalan: number;
}

const todayIso = new Date().toISOString().slice(0, 10);

function fmt(n: number) {
  return n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });
}

function normalizePhone(value?: string) {
  return (value ?? "").replace(/\s/g, "");
}

function normalizeText(value?: string) {
  return (value ?? "").trim().toLocaleLowerCase("tr-TR");
}

function findCustomerByNameOrPhone(
  customers: CustomerItem[],
  name: string,
  phone: string
): CustomerItem | null {
  const normalizedPhone = normalizePhone(phone);

  const byPhone =
    normalizedPhone.length > 0
      ? customers.find(
          (customer) => normalizePhone(customer.telefon) === normalizedPhone
        ) ?? null
      : null;

  if (byPhone) return byPhone;

  const byName =
    name.trim().length > 0
      ? customers.find(
          (customer) => normalizeText(customer.adSoyad) === normalizeText(name)
        ) ?? null
      : null;

  return byName;
}

function findShareFromAnimals(
  animals: Array<{
    id: number;
    kupeNo: string;
    hisseler: Array<{
      id: number;
      hisseNo: string;
      telefon?: string;
      musteriAdi: string;
      kalan: number;
    }>;
  }>,
  params: {
    customerPhone: string;
    customerName: string;
    hisseNo: string;
    kupeNo: string;
  }
): ShareOption | null {
  const phone = normalizePhone(params.customerPhone);
  const name = normalizeText(params.customerName);
  const hisseNo = normalizeText(params.hisseNo);
  const kupeNo = normalizeText(params.kupeNo);

  let fallback: ShareOption | null = null;

  for (const animal of animals) {
    for (const share of animal.hisseler) {
      const phoneMatch =
        phone.length > 0 && normalizePhone(share.telefon) === phone;

      const nameMatch =
        name.length > 0 && normalizeText(share.musteriAdi) === name;

      const ownerMatch = phoneMatch || nameMatch;
      if (!ownerMatch) continue;

      const option: ShareOption = {
        kupeNo: animal.kupeNo,
        hisseNo: share.hisseNo,
        animalId: animal.id,
        shareId: share.id,
        kalan: share.kalan,
      };

      if (!fallback) fallback = option;

      const hisseMatch =
        hisseNo.length > 0 && normalizeText(share.hisseNo) === hisseNo;
      const kupeMatch =
        kupeNo.length > 0 && normalizeText(animal.kupeNo) === kupeNo;

      if (hisseMatch && (kupeNo.length === 0 || kupeMatch)) {
        return option;
      }
    }
  }

  return fallback;
}

export default function EditPaymentModal({
  payment,
  onSave,
  onClose,
}: Props) {
  const { customers } = useCustomers();
  const { animals } = useAnimals();
  const { transfers: bankTransfers } = useBankTransfers();

  const isNew = !payment;
  const isPresalePayment = Boolean(payment && (Number(payment.animalId ?? 0) <= 0 || Number(payment.shareId ?? 0) <= 0 || String(payment.hisseNo ?? "").startsWith("HS-")));

  const [tarih, setTarih] = useState(payment?.tarih ?? todayIso);
  const [musteriAdi, setMusteriAdi] = useState(payment?.musteriAdi ?? "");
  const [telefon, setTelefon] = useState(payment?.telefon ?? "");
  const [hisseNo, setHisseNo] = useState(payment?.hisseNo ?? "");
  const [hayvanKupeNo, setHayvanKupeNo] = useState(payment?.hayvanKupeNo ?? "");
  const [tutar, setTutar] = useState(payment ? String(payment.tutar) : "");
  const [tip, setTip] = useState<OdemeTipi>(payment?.tip ?? "Nakit");
  const [aciklama, setAciklama] = useState(payment?.aciklama ?? "");
  const [havaleNo, setHavaleNo] = useState(payment?.havaleNo ?? "");
  const [olusturan, setOlusturan] = useState(payment?.olusturan ?? "Admin");
  const [error, setError] = useState("");
  const [transferPickerOpen, setTransferPickerOpen] = useState(false);
  const [transferDateFrom, setTransferDateFrom] = useState("");
  const [transferDateTo, setTransferDateTo] = useState("");
  const [transferDescriptionFilter, setTransferDescriptionFilter] = useState("");
  const [linkedTransferIds, setLinkedTransferIds] = useState<string[]>([]);
  const [unlinkedFisNos, setUnlinkedFisNos] = useState<string[]>([]);
  const [selectedTransferIds, setSelectedTransferIds] = useState<string[]>([]);

  const [searchQuery, setSearchQuery] = useState(payment?.musteriAdi ?? "");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerItem | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!payment) return;

    const existingCustomer = findCustomerByNameOrPhone(
      customers,
      payment.musteriAdi,
      payment.telefon
    );

    if (existingCustomer) {
      setSelectedCustomer(existingCustomer);
      setSearchQuery(existingCustomer.adSoyad);
    }
  }, [payment, customers]);

  const customerSuggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase().replace(/\s/g, "");
    if (!q) return [];

    return customers
      .filter((customer) => {
        const nameMatch = customer.adSoyad.toLowerCase().includes(q);
        const phoneMatch = normalizePhone(customer.telefon).includes(q);
        return nameMatch || phoneMatch;
      })
      .slice(0, 8);
  }, [searchQuery, customers]);

  const customerShares = useMemo<ShareOption[]>(() => {
    const matchPhone = selectedCustomer?.telefon ?? telefon;
    const matchName = selectedCustomer?.adSoyad ?? musteriAdi;

    if (!matchPhone && !matchName) return [];

    const result: ShareOption[] = [];

    for (const animal of animals) {
      for (const share of animal.hisseler ?? []) {
        const phoneMatch =
          normalizePhone(matchPhone).length > 0 &&
          normalizePhone(share.telefon) === normalizePhone(matchPhone);

        const nameMatch =
          matchName.trim().length > 0 &&
          normalizeText(share.musteriAdi) === normalizeText(matchName);

        if (phoneMatch || nameMatch) {
          result.push({
            kupeNo: animal.kupeNo,
            hisseNo: share.hisseNo,
            animalId: animal.id,
            shareId: share.id,
            kalan: share.kalan,
          });
        }
      }
    }

    return result;
  }, [animals, selectedCustomer, telefon, musteriAdi]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function highlightMatch(text: string, query: string) {
    const q = query.trim();
    if (!q) return <span>{text}</span>;

    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return <span>{text}</span>;

    return (
      <span>
        {text.slice(0, idx)}
        <mark className="bg-orange-100 text-orange-700 rounded-sm">
          {text.slice(idx, idx + q.length)}
        </mark>
        {text.slice(idx + q.length)}
      </span>
    );
  }

  function selectCustomer(customer: CustomerItem) {
    setSelectedCustomer(customer);
    setSearchQuery(customer.adSoyad);
    setMusteriAdi(customer.adSoyad);
    setTelefon(customer.telefon);
    setDropdownOpen(false);

    const shares: ShareOption[] = [];

    for (const animal of animals) {
      for (const share of animal.hisseler ?? []) {
        const phoneMatch =
          normalizePhone(share.telefon) === normalizePhone(customer.telefon);

        const nameMatch =
          normalizeText(share.musteriAdi) === normalizeText(customer.adSoyad);

        if (phoneMatch || nameMatch) {
          shares.push({
            kupeNo: animal.kupeNo,
            hisseNo: share.hisseNo,
            animalId: animal.id,
            shareId: share.id,
            kalan: share.kalan,
          });
        }
      }
    }

    if (shares.length === 1) {
      setHayvanKupeNo(shares[0].kupeNo);
      setHisseNo(shares[0].hisseNo);
    } else {
      setHayvanKupeNo("");
      setHisseNo("");
    }
  }

  function selectShare(option: ShareOption) {
    setHayvanKupeNo(option.kupeNo);
    setHisseNo(option.hisseNo);
  }

  function formatTransferDate(value?: string) {
    if (!value) return "—";
    const [year, month, day] = value.slice(0, 10).split("-");
    return year && month && day ? `${day}.${month}.${year}` : value;
  }

  function containsTransferText(value: string | undefined, query: string) {
    if (!query.trim()) return true;
    return String(value ?? "").toLocaleLowerCase("tr-TR").includes(query.trim().toLocaleLowerCase("tr-TR"));
  }

  const activeBankTransfers = useMemo(
    () =>
      bankTransfers
        .filter(isBankTransferActive)
        .filter((item) => {
          const dateOk = matchesOptionalDateRange(item.tarih, transferDateFrom, transferDateTo);
          const descriptionOk =
            containsTransferText(item.aciklama, transferDescriptionFilter) ||
            containsTransferText(item.fisNo, transferDescriptionFilter);
          return dateOk && descriptionOk;
        }),
    [bankTransfers, transferDateFrom, transferDateTo, transferDescriptionFilter]
  );

  const linkedFisNos = useMemo(
    () => havaleNo.split(",").map((item) => item.trim()).filter(Boolean),
    [havaleNo]
  );

  function toggleTransferSelection(id: string) {
    setSelectedTransferIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  function addSelectedBankTransfersToPayment() {
    const selectedItems = activeBankTransfers.filter((item) =>
      selectedTransferIds.includes(item.id)
    );

    if (selectedItems.length === 0) {
      setError("Bağlamak için en az bir havale seç.");
      return;
    }

    const current = havaleNo
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    for (const item of selectedItems) {
      if (
        !current.some(
          (value) =>
            value.toLocaleLowerCase("tr-TR") ===
            item.fisNo.toLocaleLowerCase("tr-TR")
        )
      ) {
        current.push(item.fisNo);
      }
    }

    setTip("Havale");
    setHavaleNo(current.join(", "));
    setLinkedTransferIds((prev) => {
      const next = new Set(prev);
      selectedItems.forEach((item) => next.add(item.id));
      return Array.from(next);
    });
    setUnlinkedFisNos((prev) =>
      prev.filter(
        (value) =>
          !selectedItems.some(
            (item) =>
              item.fisNo.toLocaleLowerCase("tr-TR") ===
              value.toLocaleLowerCase("tr-TR")
          )
      )
    );
    setSelectedTransferIds([]);
    setTransferPickerOpen(false);
    setError("");
  }

  function removeBankTransferFromPayment(fisNoToRemove: string) {
    const next = linkedFisNos.filter((value) => value.toLocaleLowerCase("tr-TR") !== fisNoToRemove.toLocaleLowerCase("tr-TR"));
    setHavaleNo(next.join(", "));
    setLinkedTransferIds((prev) =>
      prev.filter((id) => {
        const transfer = bankTransfers.find((item) => item.id === id);
        return transfer?.fisNo.toLocaleLowerCase("tr-TR") !== fisNoToRemove.toLocaleLowerCase("tr-TR");
      })
    );
    setUnlinkedFisNos((prev) => (prev.includes(fisNoToRemove) ? prev : [...prev, fisNoToRemove]));
    if (next.length === 0) setTip("Nakit");
  }

  function handleSave() {
    const amount = parseFloat(String(tutar).replace(",", "."));

    if (
      !tarih ||
      !musteriAdi.trim() ||
      !telefon.trim() ||
      (!isPresalePayment && (!hisseNo.trim() || !hayvanKupeNo.trim())) ||
      Number.isNaN(amount) ||
      amount <= 0
    ) {
      setError(isPresalePayment ? "Tarih, Müşteri, Telefon ve Tutar zorunludur." : "Tarih, Müşteri, Telefon, Küpe No, Hisse ve Tutar zorunludur.");
      return;
    }

    if (tip === "Havale" && !havaleNo.trim()) {
      setError("Havale seçildiğinde Havale No zorunludur.");
      return;
    }

    const matchedCustomer =
      selectedCustomer ?? findCustomerByNameOrPhone(customers, musteriAdi, telefon);

    if (isPresalePayment) {
      setError("");
      onSave({
        musteriId: matchedCustomer?.id ?? payment?.musteriId ?? 0,
        animalId: payment?.animalId ?? 0,
        shareId: payment?.shareId ?? 0,
        tarih,
        musteriAdi: matchedCustomer?.adSoyad ?? musteriAdi.trim(),
        telefon,
        hisseNo: payment?.hisseNo ?? hisseNo,
        hayvanKupeNo: payment?.hayvanKupeNo ?? hayvanKupeNo,
        tutar: amount,
        tip,
        aciklama: aciklama.trim(),
        havaleNo: tip === "Havale" && havaleNo.trim() ? havaleNo.trim() : undefined,
        fisNo: tip === "Havale" && havaleNo.trim() ? havaleNo.trim() : undefined,
        olusturan,
      }, { linkedIds: linkedTransferIds, unlinkedFisNos });
      return;
    }

    if (!matchedCustomer) {
      setError("Seçilen müşteri bulunamadı. Lütfen listeden bir müşteri seçin.");
      return;
    }

    const matchedShare = findShareFromAnimals(animals, {
      customerPhone: telefon,
      customerName: musteriAdi,
      hisseNo,
      kupeNo: hayvanKupeNo,
    });

    if (!matchedShare) {
      setError("Seçilen müşteri için uygun hisse bulunamadı.");
      return;
    }

    setError("");

    onSave({
      musteriId: matchedCustomer.id,
      animalId: matchedShare.animalId,
      shareId: matchedShare.shareId,
      tarih,
      musteriAdi: matchedCustomer.adSoyad,
      telefon,
      hisseNo: matchedShare.hisseNo,
      hayvanKupeNo: matchedShare.kupeNo,
      tutar: amount,
      tip,
      aciklama: aciklama.trim(),
      havaleNo: tip === "Havale" && havaleNo.trim() ? havaleNo.trim() : undefined,
      fisNo: tip === "Havale" && havaleNo.trim() ? havaleNo.trim() : undefined,
      olusturan,
    }, { linkedIds: linkedTransferIds, unlinkedFisNos });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-sm font-bold text-gray-800">
            {isNew ? "Yeni Ödeme Kaydı" : "Ödemeyi Düzenle"}
          </h3>
          <button
            onClick={onClose}
            type="button"
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Tarih *
              </label>
              <input
                type="date"
                value={tarih}
                onChange={(e) => setTarih(e.target.value)}
                className="w-full px-2 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-orange-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Oluşturan
              </label>
              <select
                value={olusturan}
                onChange={(e) => setOlusturan(e.target.value)}
                className="w-full px-2 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-orange-400 transition-colors bg-white cursor-pointer"
              >
                <option>Admin</option>
                <option>Muhasebe</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Müşteri Ara *
              <span className="ml-1 normal-case font-normal text-gray-400">
                — isim veya telefon yaz
              </span>
            </label>

            <div className="relative" ref={searchRef}>
              <div className="relative">
                <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 pointer-events-none">
                  <i className="ri-search-2-line text-xs" />
                </div>

                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    setDropdownOpen(true);

                    if (!value.trim()) {
                      setSelectedCustomer(null);
                      setMusteriAdi("");
                      setTelefon("");
                      setHayvanKupeNo("");
                      setHisseNo("");
                    }
                  }}
                  onFocus={() => setDropdownOpen(true)}
                  placeholder="Emre yaz → Emre Yılmaz çıksın..."
                  className="w-full pl-8 pr-3 py-2.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-orange-400 transition-colors"
                />

                {selectedCustomer && (
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-green-500">
                    <i className="ri-checkbox-circle-fill text-sm" />
                  </div>
                )}
              </div>

              {dropdownOpen && customerSuggestions.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-52 overflow-y-auto">
                  {customerSuggestions.map((customer) => {
                    const kalan = customer.toplamBorc - customer.toplamOdenen;

                    return (
                      <button
                        key={customer.id}
                        type="button"
                        onMouseDown={() => selectCustomer(customer)}
                        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-orange-50 text-left border-b border-gray-50 last:border-0 cursor-pointer transition-colors"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-800 truncate">
                            {highlightMatch(
                              customer.adSoyad,
                              searchQuery.replace(/\s/g, "").length >= 7 ? "" : searchQuery
                            )}
                          </p>
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            {highlightMatch(
                              customer.telefon,
                              searchQuery.replace(/\s/g, "").length >= 7
                                ? searchQuery.replace(/\s/g, "")
                                : ""
                            )}
                          </p>
                        </div>

                        <div className="flex-shrink-0 ml-2 text-right">
                          {customer.toplamHisseSayisi > 0 && (
                            <span className="text-[10px] text-gray-500 whitespace-nowrap">
                              {customer.toplamHisseSayisi} hisse
                            </span>
                          )}
                          {kalan > 0 && (
                            <p className="text-[10px] text-orange-600 font-semibold whitespace-nowrap">
                              {fmt(kalan)}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {dropdownOpen &&
                searchQuery.trim().length > 0 &&
                customerSuggestions.length === 0 && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-3">
                    <p className="text-[10px] text-gray-400 text-center">
                      Eşleşen müşteri bulunamadı
                    </p>
                  </div>
                )}
            </div>
          </div>

          {(musteriAdi || selectedCustomer) && (
            <div className="grid grid-cols-2 gap-2 bg-gray-50 rounded-lg p-2.5">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Ad Soyad
                </label>
                <input
                  type="text"
                  value={musteriAdi}
                  onChange={(e) => setMusteriAdi(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-orange-400 transition-colors bg-white"
                />
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={telefon}
                  onChange={(e) => setTelefon(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-orange-400 transition-colors bg-white"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Hayvan & Hisse *
              {customerShares.length > 0 && (
                <span className="ml-1 normal-case font-normal text-gray-400">
                  — seç ya da yaz
                </span>
              )}
            </label>

            {customerShares.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {customerShares.map((option) => {
                  const isActive =
                    normalizeText(hisseNo) === normalizeText(option.hisseNo) &&
                    normalizeText(hayvanKupeNo) === normalizeText(option.kupeNo);

                  return (
                    <button
                      key={`${option.animalId}-${option.shareId}`}
                      type="button"
                      onClick={() => selectShare(option)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        isActive
                          ? "bg-orange-500 border-orange-500 text-white"
                          : "bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
                      }`}
                    >
                      <i className="ri-bookmark-line text-[10px]" />
                      {option.hisseNo}
                      <span className="opacity-70">· {option.kupeNo.replace("TR-", "")}</span>
                      {option.kalan > 0 && (
                        <span className={`ml-0.5 ${isActive ? "opacity-80" : "text-orange-500"}`}>
                          {fmt(option.kalan)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Küpe No</label>
                <input
                  type="text"
                  value={hayvanKupeNo}
                  onChange={(e) => setHayvanKupeNo(e.target.value)}
                  placeholder="TR-4821-2024"
                  className="w-full px-2 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-orange-400 transition-colors font-mono"
                />
              </div>

              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Hisse No</label>
                <input
                  type="text"
                  value={hisseNo}
                  onChange={(e) => setHisseNo(e.target.value)}
                  placeholder="H-001-1"
                  className="w-full px-2 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-orange-400 transition-colors font-mono"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Tutar (₺) *
              </label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                  ₺
                </span>
                <input
                  type="number"
                  value={tutar}
                  onChange={(e) => setTutar(e.target.value)}
                  placeholder="0"
                  className="w-full pl-6 pr-2 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-orange-400 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Ödeme Tipi
              </label>
              <div className="flex gap-1">
                {(["Nakit", "Havale"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTip(t)}
                    className={`flex-1 py-2 text-[10px] font-semibold rounded-lg border-2 transition-colors cursor-pointer whitespace-nowrap ${
                      tip === t
                        ? t === "Nakit"
                          ? "bg-green-500 border-green-500 text-white"
                          : "bg-sky-500 border-sky-500 text-white"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {tip === "Havale" && (
            <div className="space-y-2 rounded-lg border border-sky-100 bg-sky-50/50 p-2.5">
              <div className="flex items-center justify-between gap-2">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                  Havale / Dekont No *
                </label>
                <button
                  type="button"
                  onClick={() => setTransferPickerOpen(true)}
                  className="rounded-full bg-sky-600 px-3 py-1.5 text-[10px] font-semibold text-white hover:bg-sky-700"
                >
                  Havale Bağla
                </button>
              </div>
              <input
                type="text"
                value={havaleNo}
                onChange={(e) => setHavaleNo(e.target.value)}
                placeholder="HVL-2024-XXXX"
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-orange-400 transition-colors font-mono"
              />
              {linkedFisNos.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {linkedFisNos.map((fisNo) => (
                    <button
                      key={fisNo}
                      type="button"
                      onClick={() => removeBankTransferFromPayment(fisNo)}
                      className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-white px-2 py-1 text-[10px] font-semibold text-sky-700 hover:bg-red-50 hover:text-red-600"
                    >
                      {fisNo}
                      <i className="ri-close-line" />
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-gray-500">
                Tutar değişmez. Bağlanan havale pasife düşer; kaldırılan havale tekrar aktif olur.
              </p>
            </div>
          )}

          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Açıklama
            </label>
            <input
              type="text"
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              placeholder="İsteğe bağlı not..."
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-orange-400 transition-colors"
            />
          </div>


          {transferPickerOpen && (
            <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-3">
              <div className="w-full max-w-4xl rounded-xl bg-white p-4 shadow-xl">
                <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
                  <div>
                    <h4 className="text-sm font-bold text-gray-800">Aktif Havale Bağla</h4>
                    <p className="text-[10px] text-gray-500">Boşta duran aktif havaleleri bu ödemeye bağlar. Birden fazla kutucuğu seçebilirsin.</p>
                  </div>
                  <button type="button" onClick={() => setTransferPickerOpen(false)} className="h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100">
                    <i className="ri-close-line" />
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <input
                    type="date"
                    value={transferDateFrom}
                    onChange={(event) => setTransferDateFrom(event.target.value)}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400"
                    title="Başlangıç tarihi"
                  />
                  <input
                    type="date"
                    value={transferDateTo}
                    onChange={(event) => setTransferDateTo(event.target.value)}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400"
                    title="Bitiş tarihi"
                  />
                  <input
                    value={transferDescriptionFilter}
                    onChange={(event) => setTransferDescriptionFilter(event.target.value)}
                    placeholder="Açıklama / Fiş No içinde ara"
                    className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400"
                  />
                </div>
                <div className="mt-3 max-h-[360px] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-100">
                  {activeBankTransfers.length === 0 ? (
                    <div className="px-4 py-8 text-center text-xs text-gray-500">Aktif havale yok.</div>
                  ) : (
                    activeBankTransfers.map((item) => {
                      const checked = selectedTransferIds.includes(item.id);

                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => toggleTransferSelection(item.id)}
                          className={`grid w-full grid-cols-[28px_86px_86px_minmax(0,1fr)_100px] items-start gap-2 px-3 py-2.5 text-left text-xs hover:bg-sky-50 ${
                            checked ? "bg-sky-50" : ""
                          }`}
                        >
                          <span className="pt-0.5">
                            <span
                              className={`flex h-4 w-4 items-center justify-center rounded border ${
                                checked
                                  ? "border-sky-600 bg-sky-600 text-white"
                                  : "border-gray-300 bg-white"
                              }`}
                            >
                              {checked && <i className="ri-check-line text-xs" />}
                            </span>
                          </span>
                          <span className="font-semibold text-gray-500">{formatTransferDate(item.tarih)}</span>
                          <span className="font-bold text-sky-700">{item.fisNo}</span>
                          <span className="whitespace-normal break-words leading-snug text-gray-700">
                            {item.aciklama || "—"}
                          </span>
                          <span className="text-right font-bold text-gray-800">
                            {item.tutar.toLocaleString("tr-TR", {
                              style: "currency",
                              currency: "TRY",
                              maximumFractionDigits: 0,
                            })}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                  <p className="text-[10px] text-gray-500">
                    {selectedTransferIds.length} havale seçildi. Açıklama tam görünür; kısaltılmaz.
                  </p>
                  <button
                    type="button"
                    onClick={addSelectedBankTransfersToPayment}
                    disabled={selectedTransferIds.length === 0}
                    className="rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Seçili Havaleleri Bağla
                  </button>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
              <i className="ri-error-warning-line flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              onClick={onClose}
              type="button"
              className="flex-1 py-2.5 text-sm border-2 border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              İptal
            </button>

            <button
              onClick={handleSave}
              type="button"
              className="flex-1 py-2.5 text-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-1.5"
            >
              <i className="ri-save-line" />
              {isNew ? "Kaydet" : "Güncelle"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}